import React, { useMemo, useRef } from "react";
// import { Checkbox } from "@material-ui/core";
import styled from "styled-components";
import MultiplierInput from "./multiplier.input";
import CustomTooltip from "../../../../app/components/tooltip/custom.tooltip";

// Memorized component for individual data cell
const DataCellComponent = React.memo(({
  assessment,
  visit,
  visitMapping,
  isChecked,
  // onCheckboxChange,
  onMultiplierChange,
  onUniqueChange,
  fieldType,
  readOnly,
}) => {
  const multiplierValue = visitMapping?.multiplier ?? "";
  const hasValue = multiplierValue && parseFloat(multiplierValue) > 0;
  
  return (
    <DataCell style={{ backgroundColor: hasValue ? "rgba(11, 116, 116, 0.2)" : "transparent" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "5px",
        width: "100%"
      }}>
        {/* <Checkbox
          style={{ padding: "0px" }}
          checked={isChecked}
          onChange={onCheckboxChange}
          disabled={readOnly}
          name="detailsMonthly"
        /> */}
        <MultiplierInput
          cellChecked={isChecked}
          fieldType={fieldType}
          onMultiplierChange={onMultiplierChange}
          onUniqueChange={onUniqueChange}
          uniqueNonUnique={visitMapping?.uniqueNonUniqueForm || ""}
          multiplier={visitMapping?.multiplier ?? ""}
          readOnly={readOnly}
        />
      </div>
    </DataCell>
  );
}, (prevProps, nextProps) => {
  return prevProps.assessment?.key === nextProps.assessment?.key &&
    prevProps.visit?.scheduleOfEventsVisitId === nextProps.visit?.scheduleOfEventsVisitId &&
    prevProps.isChecked === nextProps.isChecked &&
    prevProps.visitMapping?.multiplier === nextProps.visitMapping?.multiplier &&
    prevProps.visitMapping?.uniqueNonUniqueForm === nextProps.visitMapping?.uniqueNonUniqueForm &&
    prevProps.fieldType === nextProps.fieldType &&
    prevProps.readOnly === nextProps.readOnly;
});

DataCellComponent.displayName = 'DataCellComponent';

// Memoized component for a total cell
const TotalCellComponent = React.memo(({ value }) => (
  <TotalCell>
    <CustomTooltip title={value ?? "-"}>
      <span>{value ?? "-"}</span>
    </CustomTooltip>
  </TotalCell>
), (prevProps, nextProps) => {
  return prevProps.value === nextProps.value;
});

TotalCellComponent.displayName = 'TotalCellComponent';

// Custom comparison function for VisitColumn
const areEqual = (prevProps, nextProps) => {
  // Compare basic properties
  if (prevProps.visitIndex !== nextProps.visitIndex ||
      prevProps.fieldType !== nextProps.fieldType ||
      prevProps.visitsCount !== nextProps.visitsCount ||
      prevProps.activitiesPercentile !== nextProps.activitiesPercentile) {
    return false;
  }

  // Compare visit object
  if (prevProps.visit?.scheduleOfEventsVisitId !== nextProps.visit?.scheduleOfEventsVisitId) {
    return false;
  }

  // Validate and compare assessments array
  const prevAssessments = Array.isArray(prevProps.assessments) ? prevProps.assessments : [];
  const nextAssessments = Array.isArray(nextProps.assessments) ? nextProps.assessments : [];
  
  if (prevAssessments.length !== nextAssessments.length) {
    return false;
  }

  // Validate and compare totals array
  const prevTotals = Array.isArray(prevProps.totals) ? prevProps.totals : [];
  const nextTotals = Array.isArray(nextProps.totals) ? nextProps.totals : [];
  
  if (prevTotals.length !== nextTotals.length) {
    return false;
  }
  
  for (let i = 0; i < prevTotals.length; i++) {
    if (prevTotals[i]?.[prevProps.visitIndex] !== nextTotals[i]?.[nextProps.visitIndex]) {
      return false;
    }
  }

  // Compare checkbox states for this visit (with validations)
  const visitKey = prevProps.visit?.scheduleOfEventsVisitId;
  const prevStates = prevProps.visitCheckBoxStates || {};
  const nextStates = nextProps.visitCheckBoxStates || {};
  
  if (visitKey) {
    for (const assessment of prevAssessments) {
      const assessmentKey = assessment?.key || assessment?.scheduleOfEventsAssessmentId;
      if (!assessmentKey) continue;
      
      const prevChecked = prevStates[assessmentKey]?.[visitKey];
      const nextChecked = nextStates[assessmentKey]?.[visitKey];
      
      if (prevChecked !== nextChecked) {
        return false;
      }
    }
  }

  // Compare mappings for this visit (with validations)
  const prevMappings = prevProps.visitsAndAssessmentsMapping || {};
  const nextMappings = nextProps.visitsAndAssessmentsMapping || {};
  
  for (const assessment of prevAssessments) {
    const assessmentKey = assessment?.key || assessment?.scheduleOfEventsAssessmentId;
    if (!assessmentKey) continue;
    
    const prevMapping = prevMappings[assessmentKey];
    const nextMapping = nextMappings[assessmentKey];
    
    if (prevMapping !== nextMapping) {
      return false;
    }
  }

  return true;
};

// Optimized component to render a complete visit column
const VisitColumn = React.memo(({
  visit,
  visitIndex,
  assessments = [],
  totals = [],
  visitsAndAssessmentsMapping = {},
  visitCheckBoxStates = {},
  // handleChangeCheckbox,
  handleChange,
  // debounceOnHandleChange,
  fieldType,
  visitsCount = 1,
  activitiesPercentile = 0,
  readOnly = false,
}) => {
  // All hooks must be called before any conditional returns.
  const handlersRef = useRef({});
  
  // const getCheckboxHandler = useMemo(() => {
  //   return (assessmentKey, visitKey) => {
  //     const handlerKey = `${assessmentKey}-${visitKey}`;
  //     if (!handlersRef.current[handlerKey]) {
  //       handlersRef.current[handlerKey] = (e) => {
  //         handleChangeCheckbox(assessmentKey, visitKey, e.target.checked);
  //         debounceOnHandleChange(assessmentKey, visitKey, e.target.checked);
  //       };
  //     }
  //     return handlersRef.current[handlerKey];
  //   };
  // }, [handleChangeCheckbox, debounceOnHandleChange]);

  const getMultiplierHandler = useMemo(() => {
    return (assessmentKey, visitKey) => {
      const handlerKey = `mult-${assessmentKey}-${visitKey}`;
      if (!handlersRef.current[handlerKey]) {
        handlersRef.current[handlerKey] = (value) => {
          handleChange(assessmentKey, visitKey, value, "multiplier");
        };
      }
      return handlersRef.current[handlerKey];
    };
  }, [handleChange]);

  const getUniqueHandler = useMemo(() => {
    return (assessmentKey, visitKey) => {
      const handlerKey = `unique-${assessmentKey}-${visitKey}`;
      if (!handlersRef.current[handlerKey]) {
        handlersRef.current[handlerKey] = (value) => {
          handleChange(assessmentKey, visitKey, value, "uniqueNonUniqueForm");
        };
      }
      return handlersRef.current[handlerKey];
    };
  }, [handleChange]);

  // Critical defensive validations (after all hooks)
  if (!Array.isArray(assessments)) {
    console.error('❌ VisitColumn: assessments is not an array', assessments);
    return null;
  }

  if (!Array.isArray(totals)) {
    console.error('❌ VisitColumn: totals is not an array', totals);
    return null;
  }

  if (!visit) {
    return (
      <Column>
        {/* Empty data cells for each assessment */}
        {assessments.map((_, index) => (
          <DataCell key={`empty-data-${index}`}>-</DataCell>
        ))}
        
        {/* Empty total cells for each total */}
        {totals.map((_, index) => (
          <TotalCell key={`empty-total-${index}`}>-</TotalCell>
        ))}
      </Column>
    );
  }

  return (
    <Column>
      {/* NO HEADER - Headers are now rendered in parent TableHeader component */}

      {/* Data cells for each assessment */}
      {assessments.map((assessment, index) => {
        if (!assessment) {
          return (
            <DataCell key={`invalid-assessment-${index}`}>
              <span>Invalid assessment</span>
            </DataCell>
          );
        }
        
        const assessmentKey = assessment.key || assessment.scheduleOfEventsAssessmentId;
        if (!assessmentKey) {
          return (
            <DataCell key={`no-key-assessment-${index}`}>
              <span>No key</span>
            </DataCell>
          );
        }
        
        const assessmentMapping = visitsAndAssessmentsMapping[assessmentKey];
        const visitMapping = assessmentMapping?.find?.(
          (mapping) => mapping?.scheduleOfEventsVisitId === visit.scheduleOfEventsVisitId
        );
        const visitKey = visit.scheduleOfEventsVisitId;
        const isChecked = visitCheckBoxStates[assessmentKey]?.[visitKey] || false;

        return (
          <DataCellComponent
            key={assessmentKey}
            assessment={assessment}
            visit={visit}
            visitMapping={visitMapping}
            isChecked={isChecked}
            // onCheckboxChange={getCheckboxHandler(assessmentKey, visitKey)}
            onMultiplierChange={getMultiplierHandler(assessmentKey, visitKey)}
            onUniqueChange={getUniqueHandler(assessmentKey, visitKey)}
            fieldType={fieldType}
            readOnly={readOnly}
          />
        );
      })}

      {/* Total cells */}
      {totals.map((totalRow, index) => {
        if (!Array.isArray(totalRow)) {
          return (
            <TotalCellComponent 
              key={`invalid-total-${index}`}
              value="-"
            />
          );
        }
        
        return (
          <TotalCellComponent 
            key={`total-${index}`}
            value={totalRow[visitIndex]}
          />
        );
      })}
    </Column>
  );
}, areEqual);

VisitColumn.displayName = 'VisitColumn';

// Styled components
const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  background: white;
  flex-shrink: 0;
`;

const BaseCell = styled.div`
  height: 60px;
  min-height: 60px;
  max-height: 60px;
  border-bottom: 1px solid var(--surface-300);
  border-right: 1px solid var(--surface-300);
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-wrap: break-word;
  box-sizing: border-box;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DataCell = styled(BaseCell)`
  background: var(--White);
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
`;

const TotalCell = styled(BaseCell)`
  background: var(--White);
  font-weight: bold;
  height: 50px;
  min-height: 50px;
  max-height: 50px;
  color: var(--text-primary);
  border-top: 1px solid var(--surface-400);
  border-bottom: 1px solid var(--surface-300);
  border-right: 1px solid var(--surface-300);
`;

export default VisitColumn;